/**
 * Organoid culture request (OC) workflow helpers — labels, filters, payloads.
 */

export const OC_INDICATIONS = [
  { value: 'CRC', label: 'CRC' },
  { value: 'LC', label: 'LC' },
  { value: 'H&N', label: 'H&N' },
  { value: 'Breast', label: 'Breast' },
  { value: 'Endometrium', label: 'Endometrium' },
  { value: 'Other', label: 'Other' },
];

export const OC_PREFERRED_FORMATS = [
  { value: 'frozen_vial', label: 'Frozen vial' },
  { value: 'live_culture', label: 'Live culture' },
  { value: 'plate', label: 'Plate' },
  { value: 'slide', label: 'Slide' },
];

export const OC_KRAS_OPTIONS = [
  { value: 'WT', label: 'Wild type' },
  { value: 'G12C', label: 'G12C' },
  { value: 'G12D', label: 'G12D' },
  { value: 'G12V', label: 'G12V' },
  { value: 'Other', label: 'Other' },
  { value: 'Unknown', label: 'Unknown' },
];

/** Backend OrganoidRequestCandidate.MatchType choices (not catalog linkage). */
export const OC_CANDIDATE_MATCH_TYPES = [
  { value: 'full_match', label: 'Full match' },
  { value: 'partial_match', label: 'Partial match' },
  { value: 'possible_alternative', label: 'Possible alternative' },
  { value: 'does_not_match', label: 'Does not match' },
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'requires_additional_testing', label: 'Requires additional testing' },
];

export const OC_DEFAULT_CANDIDATE_MATCH_TYPE = 'partial_match';

/**
 * Payload for POST …/candidates/ when proposing a free-text OC id (no catalog FK).
 *
 * `review_notes` is a lab-only review note. The API no longer accepts
 * `client_visible_notes` (silently ignored) and the client portal does not return
 * the note, so it must not be presented as client-facing text.
 *
 * @param {{ free_text_oc_id: string, match_type?: string, review_notes?: string }} draft
 * @returns {{ free_text_oc_id: string, match_type: string, review_notes: string }}
 */
export function buildFreeTextCandidatePayload(draft = {}) {
  const free_text_oc_id = String(draft.free_text_oc_id || '').trim();
  const match_type =
    OC_CANDIDATE_MATCH_TYPES.some(o => o.value === draft.match_type)
      ? draft.match_type
      : OC_DEFAULT_CANDIDATE_MATCH_TYPE;
  return {
    free_text_oc_id,
    match_type,
    review_notes: String(draft.review_notes || ''),
  };
}

export const OC_STATUS_LABELS = {
  SUBMITTED: 'Submitted',
  NEEDS_CLIENT_CLARIFICATION: 'Needs client clarification',
  READY_FOR_DATA_REVIEW: 'Ready for data review',
  DATA_REVIEW_IN_PROGRESS: 'Data review in progress',
  CANDIDATES_IDENTIFIED: 'Candidates identified',
  NO_MATCHES_FOUND: 'No matches found',
  READY_FOR_TECHNICAL_REVIEW: 'Ready for technical review',
  TECHNICAL_REVIEW_IN_PROGRESS: 'Technical review in progress',
  TECHNICALLY_APPROVED: 'Technically approved',
  PARTIALLY_APPROVED: 'Partially approved',
  TECHNICAL_REVIEW_FAILED: 'Technical review failed',
  AWAITING_INTERNAL_APPROVAL: 'Awaiting internal approval',
  AWAITING_CLIENT_CONFIRMATION: 'Awaiting client confirmation',
  CLIENT_REQUESTED_CHANGES: 'Client requested changes',
  CLIENT_APPROVED: 'Client approved',
  CLIENT_DECLINED: 'Client declined',
  PREPARATION_PENDING: 'Preparation pending',
  THAWING: 'Thawing',
  CULTURE_EXPANSION: 'Culture expansion',
  QC_IN_PROGRESS: 'QC in progress',
  READY_FOR_SHIPMENT: 'Ready for shipment',
  PARTIALLY_READY: 'Partially ready',
  SHIPPED: 'Shipped',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export const OC_STAGE_LABELS = {
  client_request: 'Client request',
  admin_review: 'Administrative review',
  data_review: 'Data manager matching',
  technical_review: 'Technical culture review',
  internal_approval: 'Internal approval',
  client_response: 'Client response',
  preparation: 'Preparation and fulfilment',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Workflow / client action codes → UI copy. API payloads still use the keys. */
export const OC_ACTION_LABELS = {
  submit_request: 'Submit request',
  request_client_clarification: 'Request client clarification',
  send_to_data_review: 'Send to data review',
  start_data_review: 'Start data review',
  identify_candidates: 'Identify candidates',
  mark_no_matches: 'Mark no matches',
  send_to_technical_review: 'Send to technical review',
  start_technical_review: 'Start technical review',
  complete_technical_review: 'Submit technical review',
  return_to_data_manager: 'Return to data manager',
  return_to_technician: 'Return to technician',
  approve_client_response: 'Approve client response',
  reject_request: 'Reject request',
  cancel_request: 'Cancel request',
  start_preparation: 'Start preparation',
  confirm_on_behalf_of_client: 'Verify for the client',
  mark_thawing: 'Mark thawing',
  mark_culture_expansion: 'Mark culture expansion',
  mark_qc_in_progress: 'Mark QC in progress',
  mark_ready_for_shipment: 'Mark ready for shipment',
  mark_partially_ready: 'Mark partially ready',
  mark_as_shipped: 'Mark as shipped',
  complete_request: 'Complete request',
  revoke_client_token: 'Revoke client token',
  confirm_client_selection: 'Confirm client selection',
  client_submit_clarification: 'Client submitted clarification',
  client_request_changes: 'Client requested changes',
  client_decline: 'Client declined',
};

export const OC_CLIENT_DECISION_LABELS = {
  accepted: 'Accepted',
  rejected: 'Declined',
  declined: 'Declined',
  pending: 'Pending',
};

const OC_CODE_ACRONYMS = new Set(['oc', 'qc', 'id', 'kras', 'pdf', 'wt']);

/**
 * Turn API snake_case, kebab-case, or SCREAMING_SNAKE into sentence-style text.
 *
 * @param {string} [value]
 * @returns {string}
 */
export function humanizeOcCode(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return raw
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (OC_CODE_ACRONYMS.has(lower)) return lower.toUpperCase();
      if (index === 0) return lower.charAt(0).toUpperCase() + lower.slice(1);
      return lower;
    })
    .join(' ');
}

/**
 * User-facing label for a workflow or client action code.
 *
 * @param {string} [action]
 * @returns {string}
 */
export function getOcActionLabel(action) {
  if (action == null || String(action).trim() === '') return '—';
  const key = String(action).trim();
  return OC_ACTION_LABELS[key] || humanizeOcCode(key) || '—';
}

/**
 * User-facing label for a candidate match_type code.
 *
 * @param {string} [value]
 * @returns {string}
 */
export function getOcMatchTypeLabel(value) {
  if (value == null || String(value).trim() === '') return '';
  const key = String(value).trim();
  const found = OC_CANDIDATE_MATCH_TYPES.find(o => o.value === key);
  return found ? found.label : humanizeOcCode(key);
}

export const OC_BUCKET_OPTIONS = [
  { value: '', label: 'All buckets' },
  { value: 'waiting_client', label: 'Waiting on client' },
  { value: 'waiting_admin', label: 'Waiting on admin' },
  { value: 'waiting_data_review', label: 'Waiting data review' },
  { value: 'waiting_technical_review', label: 'Waiting technical review' },
  { value: 'ready_for_shipment', label: 'Ready for shipment' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const OC_SAMPLE_CLASSIFICATIONS = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'consortium', label: 'Consortium' },
  { value: 'unknown', label: 'Unknown' },
];

export const TECH_DECISIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'approved_with_conditions', label: 'Approved with conditions' },
  { value: 'alternative_recommended', label: 'Alternative recommended' },
  { value: 'additional_testing_required', label: 'Additional testing required' },
  { value: 'not_recommended', label: 'Not recommended' },
  { value: 'not_available', label: 'Not available' },
];

/** Backend TechnicalCultureReview.RiskLevel choices. */
export const TECH_RISK_LEVELS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const TECH_DEFAULT_RISK_LEVEL = 'medium';

/** Per-OC preparation_status labels (fulfilment ladder; no QC). */
export const OC_PREPARATION_STATUS_LABELS = {
  pending: 'Pending',
  thawing: 'Thawing',
  culture_expansion: 'Culture expansion',
  ready_for_shipment: 'Ready for shipment',
};

/**
 * Next per-OC preparation action for the given preparation_status.
 * Only the immediate next step is allowed (API rejects skips).
 *
 * @param {string} [preparationStatus]
 * @returns {{ action: string, label: string }|null}
 */
export function getNextPreparationAction(preparationStatus) {
  const map = {
    pending: 'mark_thawing',
    thawing: 'mark_culture_expansion',
    culture_expansion: 'mark_ready_for_shipment',
    ready_for_shipment: null,
  };
  if (!(preparationStatus in map)) return null;
  const action = map[preparationStatus];
  if (!action) return null;
  return { action, label: getOcActionLabel(action) };
}

/**
 * Candidates the client accepted (shown on preparation tab).
 *
 * @param {Array<Object>|null|undefined} candidates
 * @returns {Array<Object>}
 */
export function filterAcceptedCandidates(candidates) {
  if (!Array.isArray(candidates)) return [];
  return candidates.filter(c => c?.client_decision === 'accepted');
}

const TECH_APPROVED_DECISIONS = new Set([
  'approved',
  'approved_with_conditions',
]);

/**
 * Candidate the lab may accept on the client's behalf (prep verify).
 *
 * @param {Object|null|undefined} candidate
 * @returns {boolean}
 */
export function isTechnicallyApprovedCandidate(candidate) {
  if (!candidate) return false;
  return TECH_APPROVED_DECISIONS.has(candidate.technical_decision);
}

/**
 * Technically approved OCs (shown when waiting on client confirmation).
 *
 * @param {Array<Object>|null|undefined} candidates
 * @returns {Array<Object>}
 */
export function filterConfirmableCandidates(candidates) {
  if (!Array.isArray(candidates)) return [];
  return candidates.filter(isTechnicallyApprovedCandidate);
}

/**
 * Transition payload for lab `confirm_on_behalf_of_client`.
 * Same `candidate_decisions` shape as portal `confirm_client_selection`.
 *
 * @param {Array<Object>|null|undefined} candidates
 * @returns {{ candidate_decisions: Array<{ candidate_id: number, decision: string }> }}
 */
export function buildLabClientConfirmationPayload(candidates) {
  return {
    candidate_decisions: filterConfirmableCandidates(candidates)
      .filter(c => c.id != null && c.id !== '')
      .map(c => ({
        candidate_id: Number(c.id),
        decision: 'accepted',
      })),
  };
}

/**
 * Human label for preparation_status.
 *
 * @param {string} [status]
 * @returns {string}
 */
export function getPreparationStatusLabel(status) {
  if (!status) return '—';
  return OC_PREPARATION_STATUS_LABELS[status] || humanizeOcCode(status) || '—';
}

/**
 * Display OC id for prep rows.
 *
 * @param {Object} [candidate]
 * @returns {string}
 */
export function formatCandidateOcLabel(candidate = {}) {
  return (
    candidate.display_oc_id ||
    candidate.free_text_oc_id ||
    candidate.organoid_culture?.oc_id ||
    (candidate.id != null ? `#${candidate.id}` : '—')
  );
}

/**
 * Normalize a date/datetime from the API for `<input type="date">`.
 *
 * @param {string|null|undefined} value
 * @returns {string} YYYY-MM-DD or ''
 */
export function toDateInputValue(value) {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Today's date as YYYY-MM-DD (local calendar). */
export function todayDateInputValue() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Owner user id on a preparation candidate (`preparation_owner` or nested detail).
 *
 * @param {Object} [candidate]
 * @returns {number|null}
 */
export function getPreparationOwnerId(candidate = {}) {
  if (candidate.preparation_owner != null && candidate.preparation_owner !== '') {
    const id = Number(candidate.preparation_owner);
    return Number.isFinite(id) ? id : null;
  }
  const nested = candidate.preparation_owner_detail?.id;
  if (nested != null) {
    const id = Number(nested);
    return Number.isFinite(id) ? id : null;
  }
  return null;
}

/**
 * PATCH body for per-OC preparation metadata (owner + dates).
 *
 * @param {{ preparation_owner?: number|null, thawed_at?: string|null, shipped_at?: string|null }} fields
 * @returns {Object}
 */
export function buildPreparationMetaPayload(fields = {}) {
  const payload = {};
  if ('preparation_owner' in fields) {
    payload.preparation_owner =
      fields.preparation_owner == null || fields.preparation_owner === ''
        ? null
        : Number(fields.preparation_owner);
  }
  if ('thawed_at' in fields) {
    payload.thawed_at = fields.thawed_at ? String(fields.thawed_at).slice(0, 10) : null;
  }
  if ('shipped_at' in fields) {
    payload.shipped_at = fields.shipped_at
      ? String(fields.shipped_at).slice(0, 10)
      : null;
  }
  return payload;
}

/**
 * Normalize per-candidate technical review fields for POST …/technical-review/.
 *
 * @param {Object} values
 * @returns {Object}
 */
export function buildTechnicalReviewPayload(values = {}) {
  const riskOk = TECH_RISK_LEVELS.some(o => o.value === values.risk_level);
  return {
    ...values,
    risk_level: riskOk ? values.risk_level : TECH_DEFAULT_RISK_LEVEL,
    available_vials:
      values.available_vials === '' || values.available_vials == null
        ? null
        : Number(values.available_vials),
    estimated_preparation_days:
      values.estimated_preparation_days === '' ||
      values.estimated_preparation_days == null
        ? null
        : Number(values.estimated_preparation_days),
  };
}

/**
 * Whether a tech decision requires rejection_reason.
 *
 * @param {string} [decision]
 * @returns {boolean}
 */
export function techDecisionNeedsRejection(decision) {
  return decision === 'not_recommended' || decision === 'not_available';
}

const TECH_REVIEW_SUPPLY_FIELDS = [
  'storage_location',
  'current_passage',
  'growth_quality',
  'recommended_shipping_format',
  'risk_level',
  'available_vials',
  'estimated_preparation_days',
  'known_difficulties',
  'comments',
];

const TECH_REVIEW_REJECT_FIELDS = ['rejection_reason', 'comments'];

const TECH_REVIEW_NOTES_FIELDS = ['known_difficulties', 'comments'];

/**
 * Field keys to show on a tech-review card for the given decision (excludes `decision`).
 *
 * @param {string} [decision]
 * @returns {string[]}
 */
export function getTechnicalReviewVisibleFields(decision) {
  if (decision === 'approved' || decision === 'approved_with_conditions') {
    return [...TECH_REVIEW_SUPPLY_FIELDS];
  }
  if (techDecisionNeedsRejection(decision)) {
    return [...TECH_REVIEW_REJECT_FIELDS];
  }
  if (
    decision === 'alternative_recommended' ||
    decision === 'additional_testing_required'
  ) {
    return [...TECH_REVIEW_NOTES_FIELDS];
  }
  return [...TECH_REVIEW_SUPPLY_FIELDS];
}

/**
 * True when the technician has chosen a TECH_DECISIONS value.
 *
 * @param {string} [decision]
 * @returns {boolean}
 */
export function hasTechnicalReviewDecision(decision) {
  return TECH_DECISIONS.some(o => o.value === decision);
}

/** Statuses where public portal may show candidates. */
export const PUBLIC_CANDIDATE_VISIBLE_STATUSES = new Set([
  'AWAITING_CLIENT_CONFIRMATION',
  'CLIENT_APPROVED',
  'CLIENT_REQUESTED_CHANGES',
  'CLIENT_DECLINED',
  'PREPARATION_PENDING',
  'THAWING',
  'CULTURE_EXPANSION',
  'QC_IN_PROGRESS',
  'READY_FOR_SHIPMENT',
  'PARTIALLY_READY',
  'SHIPPED',
  'COMPLETED',
]);

const TOKEN_STORAGE_KEY = 'oc_request_client_access_token';

/**
 * @param {string} [status]
 * @returns {string}
 */
export function getOcStatusLabel(status) {
  if (!status) return '—';
  return OC_STATUS_LABELS[status] || humanizeOcCode(status) || '—';
}

/**
 * @param {string} [stage]
 * @returns {string}
 */
export function getOcStageLabel(stage) {
  if (!stage) return '—';
  return OC_STAGE_LABELS[stage] || humanizeOcCode(stage) || '—';
}

function labelFromChoices(choices, value) {
  if (value == null || value === '') return '';
  const found = choices.find(o => o.value === value);
  return found ? found.label : '';
}

/**
 * User-facing label for a technical-review decision code.
 *
 * @param {string} [value]
 * @returns {string}
 */
export function getOcTechDecisionLabel(value) {
  if (value == null || String(value).trim() === '') return '';
  const key = String(value).trim();
  return labelFromChoices(TECH_DECISIONS, key) || humanizeOcCode(key);
}

/**
 * User-facing label for any OC request API code shown in the UI.
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function formatOcCodeLabel(value) {
  if (value == null || String(value).trim() === '') return '—';
  const key = String(value).trim();
  if (OC_ACTION_LABELS[key]) return OC_ACTION_LABELS[key];
  if (OC_STATUS_LABELS[key]) return OC_STATUS_LABELS[key];
  if (OC_STAGE_LABELS[key]) return OC_STAGE_LABELS[key];
  if (OC_PREPARATION_STATUS_LABELS[key]) return OC_PREPARATION_STATUS_LABELS[key];
  if (OC_CLIENT_DECISION_LABELS[key]) return OC_CLIENT_DECISION_LABELS[key];
  if (OC_ASSIGNEE_ROLE_LABELS[key]) return OC_ASSIGNEE_ROLE_LABELS[key];
  const fromChoices =
    labelFromChoices(OC_CANDIDATE_MATCH_TYPES, key) ||
    labelFromChoices(TECH_DECISIONS, key) ||
    labelFromChoices(TECH_RISK_LEVELS, key) ||
    labelFromChoices(OC_SAMPLE_CLASSIFICATIONS, key) ||
    labelFromChoices(OC_PREFERRED_FORMATS, key) ||
    labelFromChoices(OC_KRAS_OPTIONS, key) ||
    labelFromChoices(OC_INDICATIONS, key) ||
    labelFromChoices(OC_BUCKET_OPTIONS, key);
  if (fromChoices) return fromChoices;
  if (/[_-]/.test(key) || key === key.toUpperCase()) {
    return humanizeOcCode(key);
  }
  return key;
}

/**
 * Bootstrap badge variant for request status.
 *
 * @param {string} [status]
 * @returns {string}
 */
export function getOcStatusBadgeVariant(status) {
  if (!status) return 'secondary';
  if (status === 'CANCELLED' || status === 'CLIENT_DECLINED' || status === 'TECHNICAL_REVIEW_FAILED') {
    return 'danger';
  }
  if (status === 'COMPLETED' || status === 'SHIPPED' || status === 'CLIENT_APPROVED') {
    return 'success';
  }
  if (
    status === 'NEEDS_CLIENT_CLARIFICATION' ||
    status === 'AWAITING_CLIENT_CONFIRMATION' ||
    status === 'AWAITING_INTERNAL_APPROVAL'
  ) {
    return 'warning';
  }
  return 'primary';
}

/**
 * Build list query string for GET /oc-requests/.
 *
 * @param {Object} [filters]
 * @returns {URLSearchParams}
 */
export function buildOcRequestListParams(filters = {}) {
  const params = new URLSearchParams();
  if (filters.pageNum) params.set('page_num', String(filters.pageNum));
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.stage) params.set('stage', filters.stage);
  if (filters.bucket) params.set('bucket', filters.bucket);
  if (filters.assignedToMe) params.set('assigned_to_me', 'true');
  return params;
}

/**
 * Build public create payload from form values.
 * Per-indication vials (FE-only) map to existing API fields:
 * - `cultures_requested` = total vials
 * - `quantity_per_culture` = "CRC: 3; LC: 2" (+ optional notes), max 128 chars
 *
 * @param {Object} values
 * @returns {Object}
 */
export function buildPublicOcCreatePayload(values = {}) {
  const indications = normalizeIndications(values.indications ?? values.indication);
  const indicationQuantities = buildIndicationQuantities(
    indications,
    values.indication_vials
  );
  const totalVials = sumIndicationVials(indicationQuantities);
  const quantityNotes = String(values.quantity_per_culture || '').trim();
  const quantityPerCulture = formatQuantityPerCultureField(
    indicationQuantities,
    quantityNotes
  );
  const payload = {
    organization: String(values.organization || '').trim(),
    contact_person: String(values.contact_person || '').trim(),
    contact_email: String(values.contact_email || '').trim(),
    contact_phone: String(values.contact_phone || '').trim(),
    project_name: String(values.project_name || '').trim(),
    purchase_order_ref: String(values.purchase_order_ref || '').trim(),
    indication: indications.length ? indications : ['Other'],
    indication_other: String(values.indication_other || '').trim(),
    cultures_requested: totalVials || Number(values.cultures_requested) || 1,
    specific_oc_numbers: String(values.specific_oc_numbers || '').trim(),
    kras_status: String(values.kras_status || '').trim(),
    other_molecular: String(values.other_molecular || '').trim(),
    treatment_history: String(values.treatment_history || '').trim(),
    passage_requirements: String(values.passage_requirements || '').trim(),
    preferred_format: values.preferred_format || '',
    quantity_per_culture: quantityPerCulture,
    required_medium: String(values.required_medium || '').trim(),
    research_purpose: String(values.research_purpose || '').trim(),
    special_requests: String(values.special_requests || '').trim(),
    mandatory_requirements: String(values.mandatory_requirements || '').trim(),
    preferred_requirements: String(values.preferred_requirements || '').trim(),
  };
  if (values.requested_delivery_date) {
    payload.requested_delivery_date = values.requested_delivery_date;
  }
  if (!payload.preferred_format) {
    delete payload.preferred_format;
  }
  return payload;
}

/**
 * Build { indicationCode: vials } for selected indications only.
 *
 * @param {string[]} indications
 * @param {Object|null|undefined} vialsMap
 * @returns {Object<string, number>}
 */
export function buildIndicationQuantities(indications = [], vialsMap = {}) {
  const map = vialsMap && typeof vialsMap === 'object' ? vialsMap : {};
  const result = {};
  indications.forEach(code => {
    const raw = map[code];
    const n = Number(raw);
    result[code] = Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
  });
  return result;
}

/**
 * @param {Object|null|undefined} quantities
 * @returns {number}
 */
export function sumIndicationVials(quantities) {
  if (!quantities || typeof quantities !== 'object') return 0;
  return Object.values(quantities).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

/**
 * Compact per-indication vial string for `quantity_per_culture` (API max 128).
 *
 * @param {Object|null|undefined} quantities
 * @param {string} [extraNotes]
 * @returns {string}
 */
export function formatQuantityPerCultureField(quantities, extraNotes = '') {
  const breakdown = formatIndicationQuantitiesDisplay(quantities);
  const notes = String(extraNotes || '').trim();
  let combined = breakdown;
  if (notes) {
    combined = breakdown ? `${breakdown} | ${notes}` : notes;
  }
  if (combined.length <= 128) return combined;
  if (breakdown.length <= 128) return breakdown;
  return breakdown.slice(0, 128);
}

/**
 * Normalize indication form/API values to a unique list of codes.
 *
 * @param {string|string[]|null|undefined} value
 * @returns {string[]}
 */
export function normalizeIndications(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map(v => String(v || '').trim()).filter(Boolean))];
  }
  if (value == null || value === '') return [];
  return [
    ...new Set(
      String(value)
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
    ),
  ];
}

/**
 * Display indication(s) from criteria (single code or comma-separated).
 *
 * @param {string|string[]|null|undefined} value
 * @returns {string}
 */
export function formatIndicationDisplay(value) {
  const list = normalizeIndications(value);
  return list.length ? list.join(', ') : '—';
}

/**
 * Display per-indication vial breakdown (compact: "CRC: 3; LC: 2").
 *
 * @param {Object|null|undefined} quantities
 * @returns {string}
 */
export function formatIndicationQuantitiesDisplay(quantities) {
  if (!quantities || typeof quantities !== 'object') return '';
  const parts = Object.entries(quantities)
    .filter(([, n]) => Number(n) > 0)
    .map(([code, n]) => `${code}: ${n}`);
  return parts.length ? parts.join('; ') : '';
}

/**
 * Parse `quantity_per_culture` ("CRC: 3; LC: 2 | notes") into counts + leftover notes.
 *
 * @param {string|null|undefined} raw
 * @returns {{ quantities: Object<string, number>, notes: string }}
 */
export function parseQuantityPerCultureBreakdown(raw) {
  const text = String(raw || '').trim();
  const quantities = {};
  const leftover = [];
  if (!text) return { quantities, notes: '' };

  const [breakdownPart, ...noteParts] = text.split('|');
  const pipeNotes = noteParts.join('|').trim();

  String(breakdownPart || '')
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .forEach(part => {
      const match = part.match(/^(.+?)\s*:\s*(\d+)\s*$/);
      if (!match) {
        leftover.push(part);
        return;
      }
      const code = match[1].trim();
      const n = Number(match[2]);
      if (code && Number.isFinite(n)) quantities[code] = n;
    });

  const notes = [leftover.join('; '), pipeNotes].filter(Boolean).join(' | ');
  return { quantities, notes };
}

/**
 * One row per requested indication for the selection-requirements panel.
 *
 * @param {Object} [criteria]
 * @returns {{ rows: Array<{ code: string, label: string, vials: number|null }>, notes: string }}
 */
export function getSelectionIndicationRows(criteria = {}) {
  const indications = normalizeIndications(criteria.indication);
  const parsed = parseQuantityPerCultureBreakdown(criteria.quantity_per_culture);
  const codes = [...indications];
  Object.keys(parsed.quantities).forEach(code => {
    if (!codes.includes(code)) codes.push(code);
  });

  const otherNote = String(criteria.indication_other || '').trim();
  const rows = codes.map(code => {
    const meta = OC_INDICATIONS.find(o => o.value === code);
    let label = meta?.label || code;
    if (code === 'Other' && otherNote) {
      label = `Other (${otherNote})`;
    }
    const vials = parsed.quantities[code];
    return {
      code,
      label,
      vials: Number.isFinite(vials) ? vials : null,
    };
  });

  return { rows, notes: parsed.notes };
}

/**
 * Build client PATCH action body.
 *
 * @param {string} action
 * @param {Object} [options]
 * @returns {Object}
 */
export function buildPublicClientActionPayload(action, options = {}) {
  const body = { action, comments: options.comments || '' };
  if (options.criteria_updates) {
    body.criteria_updates = options.criteria_updates;
  }
  if (options.candidate_decisions) {
    body.candidate_decisions = options.candidate_decisions;
  }
  return body;
}

/**
 * Build internal transition payload.
 *
 * @param {string} action
 * @param {Object} [options]
 * @returns {{ action: string, comments: string, assigned_to_id: number|null, payload: Object }}
 */
export function buildTransitionPayload(action, options = {}) {
  return {
    action,
    comments: options.comments || '',
    assigned_to_id: options.assigned_to_id ?? null,
    payload: options.payload || {},
  };
}

/**
 * Persist client access token after create.
 *
 * @param {string} token
 */
export function storeOcClientToken(token) {
  if (!token) return;
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* ignore */
  }
}

/**
 * @returns {string|null}
 */
export function readOcClientToken() {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * @param {string} [status]
 * @returns {boolean}
 */
export function canShowPublicCandidates(status) {
  return PUBLIC_CANDIDATE_VISIBLE_STATUSES.has(status);
}

/**
 * Suggested primary transition for a stage page based on current status.
 *
 * @param {string} page
 * @param {string} status
 * @returns {{ action: string, label: string }|null}
 */
export function getPrimaryTransitionForPage(page, status) {
  const map = {
    'detail': {
      SUBMITTED: { action: 'send_to_data_review', label: 'Send to data review' },
      NEEDS_CLIENT_CLARIFICATION: {
        action: 'send_to_data_review',
        label: 'Send to data review',
      },
      CLIENT_REQUESTED_CHANGES: {
        action: 'send_to_data_review',
        label: 'Send to data review',
      },
      CLIENT_APPROVED: {
        action: 'start_preparation',
        label: 'Start preparation',
      },
    },
    'data-review': {
      READY_FOR_DATA_REVIEW: {
        action: 'send_to_technical_review',
        label: 'Send to technical review',
      },
      DATA_REVIEW_IN_PROGRESS: {
        action: 'send_to_technical_review',
        label: 'Send to technical review',
      },
      CANDIDATES_IDENTIFIED: {
        action: 'send_to_technical_review',
        label: 'Send to technical review',
      },
    },
    'technical-review': {
      READY_FOR_TECHNICAL_REVIEW: {
        action: 'complete_technical_review',
        label: 'Submit technical review',
      },
      TECHNICAL_REVIEW_IN_PROGRESS: {
        action: 'complete_technical_review',
        label: 'Submit technical review',
      },
      // Revise a submitted decision (for example Not recommended) before preparation.
      AWAITING_CLIENT_CONFIRMATION: {
        action: 'complete_technical_review',
        label: 'Submit technical review',
      },
      CLIENT_APPROVED: {
        action: 'complete_technical_review',
        label: 'Submit technical review',
      },
      TECHNICAL_REVIEW_FAILED: {
        action: 'complete_technical_review',
        label: 'Submit technical review',
      },
      TECHNICALLY_APPROVED: {
        action: 'complete_technical_review',
        label: 'Submit technical review',
      },
      PARTIALLY_APPROVED: {
        action: 'complete_technical_review',
        label: 'Submit technical review',
      },
    },
    preparation: {
      AWAITING_CLIENT_CONFIRMATION: {
        action: 'confirm_on_behalf_of_client',
        label: 'Verify for the client',
      },
      CLIENT_APPROVED: { action: 'start_preparation', label: 'Start preparation' },
      // Per-OC advances use …/candidates/{id}/preparation/; not request-level mark_*.
      READY_FOR_SHIPMENT: { action: 'mark_as_shipped', label: 'Mark as shipped' },
      PARTIALLY_READY: { action: 'mark_as_shipped', label: 'Mark as shipped' },
      SHIPPED: { action: 'complete_request', label: 'Complete request' },
    },
  };
  const item = map[page]?.[status];
  if (!item) return null;
  return { action: item.action, label: getOcActionLabel(item.action) };
}

/**
 * Strip fields that must never appear in public/client UI.
 *
 * `review_notes` is a lab-only review note and the public serializer omits it.
 * Legacy `client_visible_notes` is dropped too: the name no longer means the
 * client may read it, so an older cached payload must not leak through.
 *
 * @param {Object} candidate
 * @returns {Object}
 */
export function sanitizePublicCandidate(candidate = {}) {
  const {
    internal_notes: _internalNotes,
    review_notes: _reviewNotes,
    client_visible_notes: _clientVisibleNotes,
    ...safe
  } = candidate;
  return safe;
}

/** @typedef {'admin'|'data_manager'|'technician'} OcAssigneeRole */

export const OC_ASSIGNEE_ROLE_LABELS = {
  admin: 'Administrative review',
  data_manager: 'Data manager (matching)',
  technician: 'Technician (tech review + prep)',
};

/**
 * Display label for an assignee candidate.
 *
 * @param {{ full_name?: string, username?: string, email?: string }|null|undefined} user
 * @returns {string}
 */
export function formatAssigneeOptionLabel(user) {
  if (!user) return '';
  const name = String(user.full_name || '').trim() || String(user.username || '').trim();
  return name || String(user.email || '').trim() || '';
}

/**
 * Which stage-owner role owns a status (for "waiting on" badges).
 *
 * @param {string} [status]
 * @returns {OcAssigneeRole|null}
 */
export function getOwnerRoleForStatus(status) {
  if (!status) return null;
  if (
    [
      'SUBMITTED',
      'NEEDS_CLIENT_CLARIFICATION',
      'CLIENT_REQUESTED_CHANGES',
      'CLIENT_APPROVED',
      'TECHNICAL_REVIEW_FAILED',
      // Legacy in-flight manager statuses — treat as admin-owned, not lab manager.
      'TECHNICALLY_APPROVED',
      'PARTIALLY_APPROVED',
      'AWAITING_INTERNAL_APPROVAL',
    ].includes(status)
  ) {
    return 'admin';
  }
  if (
    [
      'READY_FOR_DATA_REVIEW',
      'DATA_REVIEW_IN_PROGRESS',
      'CANDIDATES_IDENTIFIED',
      'NO_MATCHES_FOUND',
    ].includes(status)
  ) {
    return 'data_manager';
  }
  if (
    [
      'READY_FOR_TECHNICAL_REVIEW',
      'TECHNICAL_REVIEW_IN_PROGRESS',
      'PREPARATION_PENDING',
      'THAWING',
      'CULTURE_EXPANSION',
      'QC_IN_PROGRESS',
      'READY_FOR_SHIPMENT',
      'PARTIALLY_READY',
      'SHIPPED',
    ].includes(status)
  ) {
    return 'technician';
  }
  return null;
}

/**
 * Next stage-owner role for a transition action (Assign next to picker).
 *
 * @param {string} [action]
 * @returns {OcAssigneeRole|null}
 */
export function getNextAssigneeRoleForAction(action) {
  const map = {
    send_to_data_review: 'data_manager',
    start_data_review: 'data_manager',
    identify_candidates: 'data_manager',
    mark_no_matches: 'data_manager',
    send_to_technical_review: 'technician',
    start_technical_review: 'technician',
    // complete_technical_review → client (success) or admin (fail); no lab-manager handoff
    return_to_data_manager: 'data_manager',
    return_to_technician: 'technician',
    request_client_clarification: 'admin',
    start_preparation: 'technician',
    // Request-level mark_thawing / expansion / ready / QC / partially_ready are legacy bulk — not used by FE.
    mark_as_shipped: 'technician',
    complete_request: 'technician',
  };
  return map[action] || null;
}

/**
 * Request PATCH field for a role (`assignee_admin`, …).
 *
 * @param {OcAssigneeRole} role
 * @returns {string|null}
 */
export function getAssigneeFieldForRole(role) {
  const map = {
    admin: 'assignee_admin',
    data_manager: 'assignee_data_manager',
    technician: 'assignee_technician',
    lab_manager: 'assignee_lab_manager',
  };
  return map[role] || null;
}

/**
 * Nested `*_detail` object for a role on a request detail payload.
 *
 * @param {Object|null|undefined} request
 * @param {OcAssigneeRole} role
 * @returns {Object|null}
 */
export function getDetailForRole(request, role) {
  if (!request || !role) return null;
  const map = {
    admin: request.assignee_admin_detail,
    data_manager: request.assignee_data_manager_detail,
    technician: request.assignee_technician_detail,
    lab_manager: request.assignee_lab_manager_detail,
  };
  return map[role] ?? null;
}

/**
 * Current assignee user id for a role on a request.
 *
 * @param {Object|null|undefined} request
 * @param {OcAssigneeRole} role
 * @returns {number|null}
 */
export function getAssigneeIdForRole(request, role) {
  const field = getAssigneeFieldForRole(role);
  if (!request || !field) return null;
  const value = request[field];
  if (value == null || value === '') return null;
  const id = typeof value === 'object' ? value.id : value;
  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

/**
 * Human label for who is waiting (status owner).
 *
 * @param {Object|null|undefined} request
 * @returns {string}
 */
export function formatWaitingOnLabel(request) {
  if (!request) return '—';
  if (request.status === 'AWAITING_CLIENT_CONFIRMATION') {
    return 'Client';
  }
  const role = getOwnerRoleForStatus(request.status);
  if (!role) return '—';
  const detail = getDetailForRole(request, role);
  const name = formatAssigneeOptionLabel(detail);
  if (name) return name;
  return 'Unassigned (group will be notified)';
}

/**
 * Admin flag: email the client after successful technical review.
 * Missing/undefined (older payloads) defaults to true.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function seedSendClientConfirmation(value) {
  return value !== false;
}

/**
 * Toast after `complete_technical_review` based on returned request status.
 *
 * @param {string|null|undefined} status
 * @returns {{ message: string, variant: 'success'|'warning' }|null}
 */
export function getCompleteTechnicalReviewToast(status) {
  if (status === 'AWAITING_CLIENT_CONFIRMATION') {
    return {
      message: 'Technical review submitted — waiting on client',
      variant: 'success',
    };
  }
  if (status === 'CLIENT_APPROVED') {
    return {
      message:
        'Technical review submitted — OCs accepted internally (no client email). Assign workers and start preparation.',
      variant: 'success',
    };
  }
  if (status === 'TECHNICAL_REVIEW_FAILED') {
    return {
      message: 'No candidates approved — returned to administrative review',
      variant: 'warning',
    };
  }
  return null;
}

/**
 * Transition comments for the next reviewer (oldest first).
 * Empty / whitespace-only comments are omitted. Not the Comments thread.
 *
 * @param {Array<Object>|null|undefined} actions
 * @returns {Array<Object>}
 */
export function getWorkflowHandoffNotes(actions) {
  if (!Array.isArray(actions)) return [];
  const withNotes = actions.filter(
    action => action && String(action.comments || '').trim() !== ''
  );
  return [...withNotes].sort((a, b) => {
    const ta = a.created_at ? Date.parse(a.created_at) : NaN;
    const tb = b.created_at ? Date.parse(b.created_at) : NaN;
    const aOk = Number.isFinite(ta);
    const bOk = Number.isFinite(tb);
    if (aOk && bOk && ta !== tb) return ta - tb;
    if (aOk && !bOk) return -1;
    if (!aOk && bOk) return 1;
    return 0;
  });
}
